import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { useParams, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";
import SiteNav from "@/components/SiteNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ArrowLeft } from "lucide-react";

export default function QuizTake() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/quizzes/${id}`).then((r) => setQuiz(r.data)).catch((e) => {
      toast.error(errMsg(e));
      navigate("/dashboard");
    });
  }, [id]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quizzes/${id}/submit`, {
        answers: quiz.questions.map((_, i) => (answers[i] !== undefined ? answers[i] : -1)),
      });
      setResult(data);
      if (data.total > 0 && data.score / data.total >= 0.8) {
        confetti({ particleCount: 160, spread: 75, origin: { y: 0.3 } });
        setTimeout(() => confetti({ particleCount: 90, spread: 100, origin: { y: 0.4 } }), 400);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allAnswered = quiz.questions.every((_, i) => answers[i] !== undefined);

  return (
    <div className="min-h-screen" data-testid="quiz-page">
      <SiteNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="quiz-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> {t("quiz.back")}
        </Button>
        <h1 className="mt-4 text-2xl sm:text-3xl font-serif font-bold" data-testid="quiz-title">
          {lang === "hu" && quiz.title_hu ? quiz.title_hu : quiz.title}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("quiz.choose")}</p>

        {result && (
          <Card className="mt-6 border-2 border-secondary" data-testid="quiz-result">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-muted-foreground">{t("quiz.yourScore")}</p>
              <p className="text-5xl font-serif font-bold text-secondary mt-2" data-testid="quiz-score">
                {result.score}/{result.total}
              </p>
              <Button className="mt-4" variant="outline" onClick={() => { setResult(null); setAnswers({}); }} data-testid="quiz-retake-btn">
                {t("quiz.retake")}
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="mt-6 space-y-5">
          {quiz.questions.map((q, qi) => {
            const res = result?.results?.[qi];
            return (
              <Card key={qi} className={res ? (res.correct ? "border-secondary" : "border-red-300") : ""} data-testid={`quiz-question-${qi}`}>
                <CardContent className="p-5">
                  <p className="font-semibold flex items-start gap-2">
                    <span className="text-muted-foreground">{qi + 1}.</span> {q.question}
                    {res && (res.correct
                      ? <CheckCircle2 className="w-5 h-5 text-secondary shrink-0" />
                      : <XCircle className="w-5 h-5 text-red-500 shrink-0" />)}
                  </p>
                  <div className="mt-3 space-y-2">
                    {q.options.map((opt, oi) => {
                      let cls = "border-border hover:border-primary";
                      if (result) {
                        if (res?.correct_index === oi) cls = "border-secondary bg-secondary/10";
                        else if (res?.chosen === oi) cls = "border-red-400 bg-red-50";
                        else cls = "border-border opacity-60";
                      } else if (answers[qi] === oi) {
                        cls = "border-primary bg-primary/5 ring-1 ring-primary";
                      }
                      return (
                        <button
                          key={oi}
                          type="button"
                          disabled={!!result}
                          onClick={() => setAnswers({ ...answers, [qi]: oi })}
                          className={`w-full text-left rounded-lg border px-4 py-2.5 text-sm transition-colors ${cls}`}
                          data-testid={`quiz-q${qi}-option-${oi}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {res && res.explanation && (
                    <p className="mt-3 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`quiz-explanation-${qi}`}>
                      <strong>{t("quiz.explanation")}:</strong> {res.explanation}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {!result && (
          <Button className="mt-6 w-full" size="lg" disabled={!allAnswered || busy} onClick={submit} data-testid="quiz-submit-btn">
            {t("quiz.submit")}
          </Button>
        )}
      </main>
    </div>
  );
}
